import { useEffect } from "react";
import { Toaster } from "react-hot-toast";
import { Navigate, Route, Routes } from "react-router-dom";

import ErrorBoundary from "@/components/ErrorBoundary";
import DashboardLayout from "@/components/layouts/DashboardLayout";
import { SignedInLogin } from "@/components/auth/IssuerReturn";
import { LoadingSpinner } from "@/components/ui/LoadingSpinner";
import { useAuthStore } from "@/stores/authStore";
import {
  applyOrientation,
  useOrientationStore,
} from "@/stores/orientationStore";
import { watchForForegroundExpiry } from "@/utils/sessionRefresh";
import "@/utils/stability"; // Initialize stability features

// PWA Components
import OfflineBanner from "@/components/pwa/OfflineBanner";
import PWAInstallBanner from "@/components/pwa/PWAInstallBanner";
import UpdateAvailableBanner from "@/components/pwa/UpdateAvailableBanner";

// Notifications
import { ConsentRequestOverlay } from "@/components/notifications/ConsentRequestOverlay";
import { UpgradeRequiredDialog } from "@/components/subscription/UpgradeRequiredDialog";
import { ImmediateStartDialog } from "@/components/subscription/ImmediateStartDialog";
import { ConsentGate } from "@/components/consent/ConsentGate";

// Consent pages
import { ConsentRequestsPage } from "@/pages/consent/ConsentRequestsPage";

// Auth Pages
import { AffiliatePartnerAdminRegisterPage } from "@/pages/auth/AffiliatePartnerAdminRegisterPage";
import { ForgotPasswordPage } from "@/pages/auth/ForgotPasswordPage";
import { HealthcareProviderAdminRegisterPage } from "@/pages/auth/HealthcareProviderAdminRegisterPage";
import { LoginPage } from "@/pages/auth/LoginPage";
import { ProviderRegisterPage } from "@/pages/auth/ProviderRegisterPage";
import { RegisterPage } from "@/pages/auth/RegisterPage";
import { ResetPasswordPage } from "@/pages/auth/ResetPasswordPage";
import { VerifyEmailPage } from "@/pages/auth/VerifyEmailPage";
import { TourOperatorAdminRegisterPage } from "@/pages/auth/TourOperatorAdminRegisterPage";
import FirstRunPage from "@/pages/onboarding/FirstRunPage";
import SharedCardPage from "@/pages/public/SharedCardPage";

// Dashboard Pages
import { ChatPage } from "@/pages/chat/ChatPage";
import { DashboardPage } from "@/pages/dashboard/DashboardPage";
import DocumentsPage from "@/pages/documents/DocumentsPage";
import { HealthRecommendationsPage } from "@/pages/health/HealthRecommendationsPage";
import { HealthRecordsPage } from "@/pages/health/HealthRecordsPage";
import ImmunizationsPage from "@/pages/immunizations/ImmunizationsPage";
import { FHIRMedicationsPage } from "@/pages/medications/FHIRMedicationsPage";
import { MedicationAddPage } from "@/pages/medications/MedicationAddPage";
import { MedicationEditPage } from "@/pages/medications/MedicationEditPage";
import { ProfilePage } from "@/pages/profile/ProfilePage";
import { ConsentSettingsPage } from "@/pages/settings/ConsentSettingsPage";
import { GDPRSettingsPage } from "@/pages/settings/GDPRSettingsPage";
import { SettingsPage } from "@/pages/settings/SettingsPage";
import { CompliancePage } from "@/pages/account/CompliancePage";
import UnitPreferencesSettings from "@/pages/settings/UnitPreferencesSettings";
import { HealthcareFacilitiesPage } from "@/pages/travels/HealthcareFacilitiesPage";
import { ReadinessPage } from "@/pages/travels/ReadinessPage";
import { RecommendationsPage } from "@/pages/travels/RecommendationsPage";
import { RiskManagementPage } from "@/pages/travels/RiskManagementPage";
import { SegmentFormPage } from "@/pages/travels/SegmentFormPage";
import { TravelDetailPage } from "@/pages/travels/TravelDetailPage";
import PartnerOffersPage from "@/pages/travels/PartnerOffersPage";
import { TravelsPage } from "@/pages/travels/TravelsPage";
import { TripWizardPage } from "@/pages/travels/TripWizardPage";
import { VaccinationsPage } from "@/pages/travels/VaccinationsPage";

// FHIR Pages
import ExtendedFHIRDashboard from "@/pages/fhir/ExtendedFHIRDashboard";
import SimpleFHIRDashboard from "@/pages/fhir/SimpleFHIRDashboard";

// Subscription Pages
import BillingManagementPage from "@/pages/subscription/BillingManagementPage";
import SubscriptionDashboardPage from "@/pages/subscription/SubscriptionDashboardPage";
import SubscriptionPlansPage from "@/pages/subscription/SubscriptionPlansPage";
import SubscriptionSettingsPage from "@/pages/subscription/SubscriptionSettingsPage";

// Version History Page
import { AppInfoPage } from "@/pages/AppInfoPage";

// Admin Pages
import AdminPage from "@/pages/admin/AdminPage";
import AdminUsersPage from "@/pages/admin/AdminUsersPage";
import ComDeskEmailPage from "@/pages/admin/comdesk/ComDeskEmailPage";
import ComDeskPhonePage from "@/pages/admin/comdesk/PhonePage";
import ComDeskFrame from "@/pages/admin/comdesk/ComDeskFrame";
import TravellersPage from "@/pages/admin/customers/TravellersPage";
import PartnersPage from "@/pages/admin/customers/PartnersPage";
import PartnerOffersAdminPage from "@/pages/admin/customers/PartnerOffersAdminPage";
import LeadPipeline from "@/pages/admin/sales/LeadPipeline";
import SeoPage from "@/pages/admin/marketing/SeoPage";
import AioPage from "@/pages/admin/marketing/AioPage";
import CampaignsPage from "@/pages/admin/marketing/CampaignsPage";
import ContentPage from "@/pages/admin/marketing/ContentPage";
import FunnelPage from "@/pages/admin/marketing/FunnelPage";
import KeywordReportPage from "@/pages/admin/marketing/KeywordReportPage";
import FinancePage from "@/pages/admin/finance/FinancePage";
import AgentsPage from "@/pages/admin/agents/AgentsPage";
import PlatformPage from "@/pages/admin/platform/PlatformPage";
import ExecutiveDashboardPage from "@/pages/admin/dashboard/ExecutiveDashboardPage";
import DashboardDefinitionsPage from "@/pages/admin/dashboard/DefinitionsPage";
import DashboardCohortsPage from "@/pages/admin/dashboard/CohortsPage";
import SystemDashboardPage from "@/pages/admin/dashboard/SystemDashboardPage";

// Healthcare Provider Admin Pages
import { HealthcareProviderAdminPage } from "@/pages/healthcare/HealthcareProviderAdminPage";

// Tour Operator Admin Pages
import { TourOperatorAdminPage } from "@/pages/tour-admin/TourOperatorAdminPage";

// Affiliate Partner Admin Pages
import { AffiliatePartnerAdminPage } from "@/pages/affiliate-admin/AffiliatePartnerAdminPage";

// Landing Page
import { BlogPage } from "@/pages/BlogPage";
import { BlogPostPage } from "@/pages/BlogPostPage";
import { FAQPage } from "@/pages/FAQPage";
import { TravelRecordsAppPage } from "@/pages/TravelRecordsAppPage";
import { EmergencyCardToolPage } from "@/pages/EmergencyCardToolPage";
import { MedicationLetterToolPage } from "@/pages/MedicationLetterToolPage";
import { PartnerKitPage } from "@/pages/PartnerKitPage";
import { LandingPage } from "@/pages/LandingPage";
import {
  applyTheme,
  useThemeStore,
  watchSystemTheme,
} from "@/stores/themeStore";
import { AndroidInstallPage } from "@/pages/AndroidInstallPage";
import { PaymentSuccessPage } from "@/pages/PaymentSuccessPage";
import { PricingPage } from "@/pages/PricingPage";

// Legal Pages — reachable signed in and signed out; the registration form and
// the landing footer link to them, and app stores check that they resolve.
import { ContactPage } from "@/pages/legal/ContactPage";
import { ImprintPage } from "@/pages/legal/ImprintPage";
import { PrivacyPolicyPage } from "@/pages/legal/PrivacyPolicyPage";
import { SecurityPage } from "@/pages/legal/SecurityPage";
import { TermsPage } from "@/pages/legal/TermsPage";

// Emergency Pages
import EmergencyAccessDashboard from "@/pages/emergency/EmergencyAccessDashboard";
import EmergencyProfilePage from "@/pages/emergency/EmergencyProfilePage";

// Card Pages
import CardPage from "@/pages/card/CardPage";
import { MedAccessPage } from "@/pages/card/MedAccessPage";
import { MedAccessRequestsPage } from "@/pages/card/MedAccessRequestsPage";
import ShareManagementPage from "@/pages/card/ShareManagementPage";
import WalletPage from "@/pages/card/WalletPage";

// Health Pages
import AppointmentPage from "@/pages/health/AppointmentPage";
import CheckInPage from "@/pages/health/CheckInPage";
import InsuranceRequestPage from "@/pages/health/InsuranceRequestPage";

// Demo Pages
import { BookADemoPage } from "@/pages/BookADemoPage";
import DemoCompletePage from "@/pages/demo/DemoCompletePage";
import DemoPage from "@/pages/demo/DemoPage";
import { DemoBookingsPage } from "@/pages/DemoBookingsPage";

// Public Pages
import CheckinScanPage from "@/pages/public/CheckinScanPage";
import { log } from "@/lib/log";
import { FunnelTracker } from "@/components/FunnelTracker";

function App() {
  const { user, isLoading, isAuthenticated, initializeAuth } = useAuthStore();

  useEffect(() => {
    initializeAuth();
  }, []); // Run only once on mount

  // A phone that has been asleep wakes up holding a token that expired hours
  // ago. Renew it as the app comes forward, before anything asks for data.
  useEffect(() => watchForForegroundExpiry(), []);

  // Debug logging - only in development and when state changes meaningfully
  useEffect(() => {
    if (import.meta.env.DEV && (isAuthenticated || isLoading)) {
      log.debug("🔍 App.tsx Auth State:", {
        hasUser: !!user,
        isAuthenticated,
        isLoading,
        userEmail: user?.email,
      });
    }
  }, [user, isAuthenticated, isLoading]);

  // Applied on mount as well as on change: the persisted store rehydrates after
  // first paint, so without this the page starts in the wrong theme.
  const themeMode = useThemeStore((state) => state.mode);
  useEffect(() => {
    applyTheme(themeMode);
    return watchSystemTheme();
  }, [themeMode]);

  // Same reason, and the same fix: the orientation lock has to be re-asserted
  // on every load, because a lock does not survive the page it was set on.
  const orientationMode = useOrientationStore((state) => state.mode);
  useEffect(() => {
    applyOrientation(orientationMode);
  }, [orientationMode]);

  if (isLoading) {
    return (
      <div
        className="min-h-screen flex items-center justify-center bg-transparent"
        aria-label="Loading"
        aria-busy="true"
      >
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <ErrorBoundary>
      {/* PWA Components - Global */}
      <OfflineBanner />
      <UpdateAvailableBanner />
      <PWAInstallBanner />

      {/* The launch funnel's page steps, signed-out visitors only */}
      <FunnelTracker signedIn={!!user && isAuthenticated} />

      <Routes>
        {/* Public Routes */}
        {!user || !isAuthenticated ? (
          <>
            {/* Landing Page */}
            <Route path="/" element={<LandingPage />} />
            <Route path="/faq" element={<FAQPage />} />
            <Route
              path="/travel-medical-records-app"
              element={<TravelRecordsAppPage />}
            />
            <Route
              path="/emergency-medical-card"
              element={<EmergencyCardToolPage />}
            />
            <Route
              path="/medication-travel-letter"
              element={<MedicationLetterToolPage />}
            />
            <Route path="/partner-kit" element={<PartnerKitPage />} />

            {/* Legal pages. /gdpr is an older link in the landing footer and
                is answered by the privacy notice rather than a 404. */}
            <Route path="/privacy" element={<PrivacyPolicyPage />} />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/imprint" element={<ImprintPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/security" element={<SecurityPage />} />
            <Route path="/gdpr" element={<Navigate to="/privacy" replace />} />
            <Route path="/blog" element={<BlogPage />} />
            <Route path="/blog/:slug" element={<BlogPostPage />} />
            <Route path="/pricing" element={<PricingPage />} />
            <Route path="/payment-success" element={<PaymentSuccessPage />} />
            <Route path="/book-a-demo" element={<BookADemoPage />} />

            {/* Android install guide - the target of the QR codes on the
                landing page, so it must resolve before any login redirect. */}
            <Route path="/android-install" element={<AndroidInstallPage />} />

            {/* Emergency Access - Public (No Auth Required) */}
            {/* A card shared by link (card.service.ts rowToShare) */}
            <Route path="/card/share/:token" element={<SharedCardPage />} />
            {/* The QR on every card encodes /emergency/qr/<token>
                (card-fastify.ts). Until 2026-10-06 no route matched it, and
                a responder scanning a card landed on the login page. */}
            <Route
              path="/emergency/qr/:token"
              element={<EmergencyAccessDashboard />}
            />
            <Route
              path="/emergency/access/:token"
              element={<EmergencyAccessDashboard />}
            />
            <Route
              path="/emergency/access"
              element={<EmergencyAccessDashboard />}
            />

            {/* Check-in Scan – Public, no login */}
            <Route path="/checkin/:token" element={<CheckinScanPage />} />

            {/* Med Access – Public landing for doctors scanning QR */}
            <Route path="/med-access/:token" element={<MedAccessPage />} />

            {/* Email confirmation - the target of the link we mail out, so it
                has to resolve before any login redirect. Present in the
                signed-in branch too: the link is opened from a mail client and
                there is no telling which state the reader is in. */}
            <Route path="/verify-email" element={<VerifyEmailPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />

            {/* Traveler Auth Routes */}
            <Route path="/traveler/login" element={<LoginPage />} />
            <Route path="/traveler/register" element={<RegisterPage />} />
            <Route
              path="/traveler/forgot-password"
              element={<ForgotPasswordPage />}
            />

            {/* Legacy Auth Routes - redirect to traveler */}
            <Route
              path="/auth/login"
              element={<Navigate to="/traveler/login" replace />}
            />
            <Route
              path="/auth/forgot-password"
              element={<Navigate to="/traveler/forgot-password" replace />}
            />

            {/* Provider Registration Routes */}
            <Route
              path="/register/provider"
              element={<ProviderRegisterPage />}
            />
            <Route
              path="/register/healthcare-provider-admin"
              element={<HealthcareProviderAdminRegisterPage />}
            />
            <Route
              path="/register/tour-operator-admin"
              element={<TourOperatorAdminRegisterPage />}
            />
            <Route
              path="/register/affiliate-partner-admin"
              element={<AffiliatePartnerAdminRegisterPage />}
            />

            {/* Redirect all other routes to traveler login when not authenticated */}
            <Route
              path="*"
              element={<Navigate to="/traveler/login" replace />}
            />
          </>
        ) : (
          <>
            {/* First run (UX_CONCEPT.md §5.5) – full screen, outside the app
                shell, where registration lands */}
            <Route path="/traveler/welcome" element={<FirstRunPage />} />
            <Route path="/card/share/:token" element={<SharedCardPage />} />
            {/* Emergency access – a doctor or responder may be signed in
                to their own account when they scan a card */}
            <Route
              path="/emergency/qr/:token"
              element={<EmergencyAccessDashboard />}
            />
            <Route
              path="/emergency/access/:token"
              element={<EmergencyAccessDashboard />}
            />
            {/* Check-in Scan – also accessible when logged in */}
            <Route path="/checkin/:token" element={<CheckinScanPage />} />
            {/* Public pages also accessible when logged in */}
            <Route path="/book-a-demo" element={<BookADemoPage />} />
            <Route path="/android-install" element={<AndroidInstallPage />} />
            {/* Legal pages – a signed-in user must be able to read the terms
                they are being billed under, and the settings pages link here */}
            <Route path="/privacy" element={<PrivacyPolicyPage />} />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/imprint" element={<ImprintPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/security" element={<SecurityPage />} />
            <Route path="/gdpr" element={<Navigate to="/privacy" replace />} />
            {/* Email confirmation – the link may well be opened by someone who
                is already signed in on this device */}
            <Route path="/verify-email" element={<VerifyEmailPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            {/* Med Access QR landing – also works when logged in (doctor) */}
            <Route path="/med-access/:token" element={<MedAccessPage />} />

            {/* These five were registered for signed-out visitors only, and
                React Router renders nothing for a path it does not know: a
                signed-in traveller opening any of them got a blank white page.
                /payment-success is the worst of them — it is the URL Stripe
                returns a customer to after a successful payment, and the
                customer is signed in by definition, so every paid checkout
                ended on a blank screen. */}
            <Route path="/payment-success" element={<PaymentSuccessPage />} />
            <Route path="/pricing" element={<PricingPage />} />
            <Route path="/faq" element={<FAQPage />} />
            <Route
              path="/travel-medical-records-app"
              element={<TravelRecordsAppPage />}
            />
            <Route
              path="/emergency-medical-card"
              element={<EmergencyCardToolPage />}
            />
            <Route
              path="/medication-travel-letter"
              element={<MedicationLetterToolPage />}
            />
            <Route path="/partner-kit" element={<PartnerKitPage />} />
            <Route path="/blog" element={<BlogPage />} />
            <Route path="/blog/:slug" element={<BlogPostPage />} />

            {/* Protected Routes */}
            <Route path="/" element={<DashboardLayout />}>
              <Route index element={<DashboardPage />} />
              <Route path="dashboard" element={<DashboardPage />} />
              <Route path="health" element={<DashboardPage />} />
              <Route path="traveler/profile" element={<ProfilePage />} />
              <Route path="traveler/health/*" element={<HealthRecordsPage />} />
              <Route
                path="traveler/health-recommendations/:countryCode"
                element={<HealthRecommendationsPage />}
              />
              <Route
                path="traveler/medications"
                element={<FHIRMedicationsPage />}
              />
              <Route
                path="traveler/medications/add"
                element={<MedicationAddPage />}
              />
              <Route
                path="traveler/medications/:id/edit"
                element={<MedicationEditPage />}
              />
              <Route
                path="traveler/immunizations/*"
                element={<ImmunizationsPage />}
              />
              <Route path="traveler/travels" element={<TravelsPage />} />
              <Route path="traveler/travels/new" element={<TripWizardPage />} />
              <Route
                path="traveler/travels/:id"
                element={<TravelDetailPage />}
              />
              <Route
                path="traveler/travels/:id/partner-offers"
                element={<PartnerOffersPage />}
              />
              <Route
                path="traveler/travels/:travelId/segments/:segmentId"
                element={<SegmentFormPage />}
              />
              <Route
                path="traveler/travel/:id/riskmanagement"
                element={<RiskManagementPage />}
              />
              <Route
                path="traveler/travel/:id/vaccinations"
                element={<VaccinationsPage />}
              />
              <Route
                path="traveler/travel/:id/readiness"
                element={<ReadinessPage />}
              />
              <Route
                path="traveler/travel/:id/recommendations"
                element={<RecommendationsPage />}
              />
              <Route
                path="traveler/travel/:id/facilities"
                element={<HealthcareFacilitiesPage />}
              />
              <Route path="traveler/documents/*" element={<DocumentsPage />} />
              <Route path="traveler/chat/*" element={<ChatPage />} />
              <Route path="traveler/settings/*" element={<SettingsPage />} />

              {/* Health Tab Routes */}
              <Route
                path="traveler/health/emergency"
                element={<EmergencyProfilePage />}
              />

              {/* Card Routes */}
              <Route path="traveler/card" element={<CardPage />} />
              <Route path="traveler/card/wallet" element={<WalletPage />} />
              <Route
                path="traveler/card/shares"
                element={<ShareManagementPage />}
              />
              <Route
                path="traveler/med-access-requests"
                element={<MedAccessRequestsPage />}
              />

              {/* Consent Request Routes (REQ-008) */}
              <Route
                path="traveler/consent-requests"
                element={<ConsentRequestsPage />}
              />
              <Route
                path="traveler/health/appointment"
                element={<AppointmentPage />}
              />
              <Route path="traveler/health/checkin" element={<CheckInPage />} />
              <Route
                path="traveler/health/insurance-request"
                element={<InsuranceRequestPage />}
              />

              {/* Demo Routes */}
              <Route path="demo" element={<DemoPage />} />
              <Route path="demo/complete" element={<DemoCompletePage />} />

              {/* Subscription Routes */}
              <Route
                path="traveler/subscription"
                element={<SubscriptionDashboardPage />}
              />
              <Route
                path="traveler/subscription/plans"
                element={<SubscriptionPlansPage />}
              />
              <Route
                path="traveler/subscription/billing"
                element={<BillingManagementPage />}
              />
              <Route
                path="traveler/subscription/settings"
                element={<SubscriptionSettingsPage />}
              />
              {/* Account: the plan, payment and invoices above, Compliance here. */}
              <Route
                path="traveler/account"
                element={<Navigate to="/traveler/subscription" replace />}
              />
              <Route
                path="traveler/account/compliance"
                element={<CompliancePage />}
              />
              <Route
                path="fhir/*"
                element={
                  <ErrorBoundary
                    fallbackTitle="FHIR Dashboard Fehler"
                    fallbackMessage="Das FHIR Dashboard konnte nicht geladen werden."
                  >
                    <SimpleFHIRDashboard />
                  </ErrorBoundary>
                }
              />
              <Route
                path="fhir-extended"
                element={
                  <ErrorBoundary
                    fallbackTitle="Extended FHIR Dashboard Fehler"
                    fallbackMessage="Das erweiterte FHIR Dashboard konnte nicht geladen werden."
                  >
                    <ExtendedFHIRDashboard />
                  </ErrorBoundary>
                }
              />

              {/* App Info. The old version-history page listed a hand-written
                  set of releases that stopped being updated, so it described a
                  version nobody was running; it now redirects here, where the
                  list is generated from the commits. */}
              <Route path="app-info" element={<AppInfoPage />} />
              <Route
                path="version-history"
                element={<Navigate to="/app-info" replace />}
              />

              {/* Old subscription routes for backwards compatibility */}
              <Route
                path="subscription"
                element={<SubscriptionDashboardPage />}
              />
              <Route
                path="subscription/plans"
                element={<SubscriptionPlansPage />}
              />
              <Route
                path="subscription/billing"
                element={<BillingManagementPage />}
              />
              <Route
                path="subscription/settings"
                element={<SubscriptionSettingsPage />}
              />

              {/* Old settings route for backwards compatibility */}
              <Route path="settings/*" element={<SettingsPage />} />
              <Route
                path="settings/consents"
                element={<ConsentSettingsPage />}
              />
              <Route path="settings/gdpr" element={<GDPRSettingsPage />} />
              {/* Was implemented but never routed, so no URL rendered it. */}
              <Route
                path="settings/units"
                element={<UnitPreferencesSettings />}
              />
              <Route
                path="traveler/settings/units"
                element={<UnitPreferencesSettings />}
              />

              {/* Old profile route for backwards compatibility */}
              <Route path="profile" element={<ProfilePage />} />

              {/* Admin.
                  Every administrative page lives under /admin, so there is one
                  address to remember and one place to protect. They used to be
                  scattered across /healthcare-admin, /tour-admin,
                  /affiliate-admin and /demo-bookings, each its own top-level
                  path with nothing tying them together. The old paths redirect
                  rather than disappear — they are in bookmarks and in mail.

                  The landing page is /admin/dashboard, not /admin, so that
                  every administrative page is a named page and /admin is a
                  section rather than a page that happens to also be a section.
                  /admin redirects to it. */}
              <Route
                path="admin"
                element={<Navigate to="/admin/dashboard" replace />}
              />
              <Route path="admin/dashboard" element={<AdminPage />} />
              {/* REQ-021: the figures the company acts on, and the record of
                  what each one means. Two pages rather than one, because the
                  definitions have to be readable without a number in front of
                  them — that is what makes them checkable. */}
              <Route
                path="admin/dashboard/executive"
                element={<ExecutiveDashboardPage />}
              />
              <Route
                path="admin/dashboard/definitions"
                element={<DashboardDefinitionsPage />}
              />
              {/* AC-004: the same two customer measures by signup month, which
                  is the comparison the executive screen's aggregates hide. */}
              <Route
                path="admin/dashboard/cohorts"
                element={<DashboardCohortsPage />}
              />
              {/* The per-system screens: every metric in a group, not only the
                  ten that reach the executive screen. One parameterised route
                  rather than seven, so a group added to the metric registry
                  arrives with a screen instead of arriving unreachable. The
                  `system/` segment keeps it clear of the static siblings
                  above. */}
              <Route
                path="admin/dashboard/system/:group"
                element={<SystemDashboardPage />}
              />
              <Route path="admin/users" element={<AdminUsersPage />} />
              {/* Every back-office section is reachable at the path it owns.
                  These four had pages but no base route, so /admin/comdesk and
                  its siblings fell to the catch-all below and dropped a staff
                  member onto the traveller dashboard — indistinguishable from
                  being signed out, and that is how it was reported. Each lands
                  on the section's first built page rather than on an overview
                  invented for the purpose. The other four sections already
                  resolve: /admin/dashboard, /admin/finance, /admin/agents and
                  /admin/platform are pages in their own right. */}
              <Route
                path="admin/comdesk"
                element={<Navigate to="/admin/comdesk/dashboard" replace />}
              />
              <Route
                path="admin/customers"
                element={<Navigate to="/admin/customers/travellers" replace />}
              />
              <Route
                path="admin/sales"
                element={<Navigate to="/admin/sales/leads" replace />}
              />
              <Route
                path="admin/marketing"
                element={<Navigate to="/admin/marketing/seo" replace />}
              />
              {/* BIZZMATIX ComDesk, framed: every surface and anything below
                  it (/admin/comdesk/mail/<thread>). The built-in Email screen
                  keeps its address, which alerts and calendar mails link to,
                  and the built-in Phone screen moves to legacy/phone, until
                  mail and phone run in ComDesk (docs/operations/comdesk.md).
                  Neither is in the menu any more. */}
              <Route
                path="admin/comdesk/email"
                element={<ComDeskEmailPage />}
              />
              <Route
                path="admin/comdesk/legacy/phone"
                element={<ComDeskPhonePage />}
              />
              <Route path="admin/comdesk/:surface" element={<ComDeskFrame />} />
              <Route
                path="admin/comdesk/:surface/*"
                element={<ComDeskFrame />}
              />
              <Route
                path="admin/customers/travellers"
                element={<TravellersPage />}
              />
              <Route
                path="admin/customers/partners"
                element={<PartnersPage />}
              />
              <Route
                path="admin/customers/partner-offers"
                element={<PartnerOffersAdminPage />}
              />
              {/* One component, two routes: inbound and outbound are the same
                  record with different provenance (REQ-017). */}
              <Route
                path="admin/sales/leads"
                element={<LeadPipeline kind="inbound" />}
              />
              <Route
                path="admin/sales/targets"
                element={<LeadPipeline kind="outbound" />}
              />
              <Route path="admin/marketing/seo" element={<SeoPage />} />
              <Route path="admin/marketing/aio" element={<AioPage />} />
              <Route
                path="admin/marketing/campaigns"
                element={<CampaignsPage />}
              />
              <Route path="admin/marketing/content" element={<ContentPage />} />
              <Route path="admin/marketing/funnel" element={<FunnelPage />} />
              <Route
                path="admin/marketing/keywords"
                element={<KeywordReportPage />}
              />
              <Route path="admin/finance" element={<FinancePage />} />
              <Route path="admin/agents" element={<AgentsPage />} />
              <Route path="admin/platform" element={<PlatformPage />} />
              <Route
                path="admin/demo-bookings"
                element={<DemoBookingsPage />}
              />
              <Route
                path="admin/healthcare"
                element={<HealthcareProviderAdminPage />}
              />
              <Route path="admin/tours" element={<TourOperatorAdminPage />} />
              <Route
                path="admin/affiliates"
                element={<AffiliatePartnerAdminPage />}
              />

              <Route
                path="healthcare-admin"
                element={<Navigate to="/admin/healthcare" replace />}
              />
              <Route
                path="tour-admin"
                element={<Navigate to="/admin/tours" replace />}
              />
              <Route
                path="affiliate-admin"
                element={<Navigate to="/admin/affiliates" replace />}
              />
              <Route
                path="demo-bookings"
                element={<Navigate to="/admin/demo-bookings" replace />}
              />
            </Route>

            {/* Redirect auth routes to dashboard */}
            <Route path="/auth/*" element={<Navigate to="/" replace />} />

            {/* Signed in already: the dashboard, or back to ComDesk's sign-in */}
            <Route path="/login" element={<SignedInLogin />} />

            {/* A path this branch does not know used to render nothing at all,
                because the only catch-all lives in the signed-out branch. An
                unknown URL now lands on the dashboard rather than on a blank
                page. */}
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </>
        )}
      </Routes>

      {/* Consent Request Overlay – global, shown on any page when a request arrives */}
      <ConsentRequestOverlay />

      {/* A 402 from any paid route, turned into the offer to upgrade */}
      <UpgradeRequiredDialog />

      {/* Start-now confirmation before every checkout (utils/immediateStart) */}
      <ImmediateStartDialog />

      {/* The sign-up consents: asked once of accounts that have not answered
          them, and offered in place when a request is refused for lack of one */}
      {user && isAuthenticated && <ConsentGate />}

      {/* Toast notifications */}
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 4000,
          style: {
            background: "#363636",
            color: "#fff",
          },
          success: {
            style: {
              background: "#10b981",
            },
          },
          error: {
            style: {
              background: "#ef4444",
            },
          },
        }}
      />
    </ErrorBoundary>
  );
}

export default App;
